import Api, { handleData, handleError } from '../Api';
import request from '../request';

import { url } from './config';
import { CreateTravelFile, TableTravelFilesBody, TravelFile, CreateManualTravelFile } from './oldModelTypes';
import {
    BodyAddServiceToTravelFile,
    BodyAmendTravelFileService,
    BodyUpdateRoomsGroupTravelFile,
    BodyUpdateTravelFileRemarks,
    BuiltUpdateTravelFileService,
    QueryGetTourServicePassengersByTravelFile,
    QueryGetTourPassengersByTourId,
    BodyQueryTFType
} from './context/bodyParamsTypes';
import { clearTravelFilesQueryCache } from './travelFilesQueryCache';
import type { TourPriceBreakdown } from './Services/tourPriceBreakdown.types';

/** A paid booking whose price would no longer match its rooms — see pg.checkRoomsPrice. */
export type RoomPriceMismatch = {
    travelFileId: number;
    refNumber: string;
    passengerNames: string[];
    currency: number | null;
    paid: number;
    wouldBe: number;
};

export type AdoptTbTravelFileBody = {
    tbTravelFileId: number | string;
    targetTourId: number | string;
    /** classes._id */
    targetClassId: number | string;
    overrideCapacity?: boolean;
};

class ExtendedApi extends Api<TravelFile> {
    async query(body: BodyQueryTFType) {
        try {
            const response = await request.post(`${url}-query`, body);
            return handleData(response);
        } catch (error) {
            return handleError(error);
        }
    }
    getTravelFilesByTicketNumber(query: any) {
        return request
            .get(`${url}-search-by-ticket-number`, { params: query })
            .then((res) => handleData(res))
            .catch((error) => handleError(error));
    }
    getTourServicePassengersByTravelFile(query: QueryGetTourServicePassengersByTravelFile) {
        return request
            .get(`${url}-passengers-by-tour-service`, { params: query })
            .then((res) => handleData(res))
            .catch((error) => handleError(error));
    }
    getTourPassengersByTourId(query: QueryGetTourPassengersByTourId) {
        return request
            .get(`${url}-passengers-by-tour-id`, { params: query })
            .then((res) => handleData(res))
            .catch((error) => handleError(error));
    }
    createTravelFileByService(body: CreateTravelFile) {
        clearTravelFilesQueryCache();
        return request.post(`${url}-by-service`, body).then(handleData).catch(handleError);
    }
    createTravelFileNoService(body: CreateManualTravelFile) {
        clearTravelFilesQueryCache();
        return request.post(`${url}-no-service`, body).then(handleData).catch(handleError);
    }
    /** What moving a TravelBooster file would do — nothing is written. See adoptTbTravelFile.ts. */
    previewAdoptTbTravelFile(body: AdoptTbTravelFileBody) {
        return request.post(`${url}-adopt-preview`, body, { timeout: 60000 }).then(handleData).catch(handleError);
    }
    /** Move a TravelBooster file onto a Taytan tour. A refusal is a 409 carrying the plan. */
    adoptTbTravelFile(body: AdoptTbTravelFileBody) {
        clearTravelFilesQueryCache();
        return request.post(`${url}-adopt`, body, { timeout: 60000 }).then(handleData).catch(handleError);
    }
    /** Where a TravelBooster file was moved to, or null. */
    getTbTravelFileAdoption(tbTravelFileId: number | string) {
        return request
            .get(`${url}-adoption`, { params: { tbTravelFileId } })
            .then(handleData)
            .catch(handleError);
    }
    /** The TravelBooster file a Taytan copy was moved from, or null. */
    getAdoptionForCopy(appTravelFileId: number | string) {
        return request
            .get(`${url}-adoption`, { params: { appTravelFileId } })
            .then(handleData)
            .catch(handleError);
    }
    /** What "Update from TravelBooster" would change on a moved file — nothing is written. See updateAdoptedTravelFile.ts. */
    previewUpdateAdoptedTravelFile(body: { appTravelFileId: number | string; overrideCapacity?: boolean }) {
        return request.post(`${url}-adopt-update-preview`, body, { timeout: 60000 }).then(handleData).catch(handleError);
    }
    /** Bring a moved file in line with TravelBooster. A refusal is a 409 carrying the plan. */
    updateAdoptedTravelFile(body: { appTravelFileId: number | string; overrideCapacity?: boolean }) {
        clearTravelFilesQueryCache();
        return request.post(`${url}-adopt-update`, body, { timeout: 60000 }).then(handleData).catch(handleError);
    }
    /** Open an In Process file with no payment; a refusal is a 409 with a message. */
    confirmWithoutPayment(body: { travelFileId: number | string; reason: string; noCharge: boolean }) {
        clearTravelFilesQueryCache();
        return request.post(`${url}-confirm-without-payment`, body).then(handleData).catch(handleError);
    }
    /** Send the travel file to the CRM again (travel_file:send_to_crm). */
    sendTravelFileToCrm(travelFileId: number | string) {
        return request.post(`${url}-send-to-crm`, { travelFileId }).then(handleData).catch(handleError);
    }
    createManualTravelFileByPNR(body: CreateManualTravelFile) {
        clearTravelFilesQueryCache();
        return request.post(`${url}-by-pnr`, body).then(handleData).catch(handleError);
    }
    getTravelFile(query: string | null) {
        return request.get(`${url}-get-travel-file`, { params: query }).then(handleData).catch(handleError);
    }
    // On-demand customer "View My Booking" portal link for a TF (the same link
    // emailed to the customer). Generated server-side from a self-contained
    // token — never persisted. Returns `{ url }`.
    getBookingLink(id: string | number): Promise<{ url: string }> {
        return request.get(`${url}-booking-link/${id}`).then(handleData).catch(handleError);
    }
    // Email the customer their booking link with a custom subject/message.
    sendClientMail(body: {
        travelFileId: string | number;
        to: string;
        subject?: string;
        message?: string;
    }): Promise<{ ok: boolean }> {
        return request.post(`${url}-send-client-mail`, body).then(handleData).catch(handleError);
    }
    /** Exactly what `sendClientMail` would deliver, rendered but not sent. */
    previewClientMail(body: {
        travelFileId: string | number;
        subject?: string;
        message?: string;
    }): Promise<{ html: string; subject: string }> {
        return request.post(`${url}-preview-client-mail`, body).then(handleData).catch(handleError);
    }
    getTableTravelFiles(body: TableTravelFilesBody) {
        return request.post(`${url}-table`, body).then(handleData).catch(handleError);
    }
    getTravelFiles(query: any) {
        return request.get(`${url}-search`, { params: query }).then(handleData).catch(handleError);
    }
    getTravelFilesByIds(ids: Array<string | number>, options: { withUnexportedCards?: boolean } = {}) {
        return request.post(`${url}-by-ids`, { ids, ...options }).then(handleData).catch(handleError);
    }
    /** The Hashavshevet export's travel-file numbering: missing numbers from travelFileLedgerFrom on. */
    getLedgerNumbering() {
        return request.get(`${url}-ledger-numbering`).then(handleData).catch(handleError);
    }
    markTravelFilesLedgerExported(ids: Array<string | number>) {
        return request.put(`${url}-mark-ledger-exported`, { ids }).then(handleData).catch(handleError);
    }
    getTravelFilesByTourDetails(query: any) {
        return request.get(`${url}-search-by-tour`, { params: query }).then(handleData).catch(handleError);
    }
    getTravelFilesByInventory(query: any) {
        return request
            .get(`${url}-search-by-inventory`, { params: query })
            .then((res) => handleData(res))
            .catch((error) => handleError(error));
    }
    getTravelFilesByPassengerDetails(query: any) {
        return request
            .get(`${url}-search-by-passenger`, { params: query })
            .then((res) => handleData(res))
            .catch((error) => handleError(error));
    }
    getSalesAndProfit(query: any) {
        return request.get(`${url}-sales-profit`, { params: query }).then(handleData).catch(handleError);
    }
    getSalesAndProfitSummary(query: any) {
        return request
            .get(`${url}-sales-profit-summary`, { params: query })
            .then(handleData)
            .catch(handleError);
    }
    getReservationsReport(query: any) {
        return request
            .get(`${url}-reservations-report`, { params: query })
            .then(handleData)
            .catch(handleError);
    }
    getLeftToPayReport(query: any) {
        return this.request
            .get(`${url}-left-to-pay-report`, { params: query })
            .then(handleData)
            .catch(handleError);
    }
    getCancelledPassengersReport(query: any) {
        return request
            .get(`${url}-cancelled-passengers-report`, { params: query })
            .then(handleData)
            .catch(handleError);
    }
    /** Counts per month for the dashboard chart — see getCancelledPassengersMonthlyPg. */
    getCancelledPassengersMonthly(query: { fromCancelDate: string; toCancelDate: string }) {
        return request
            .get(`${url}-cancelled-passengers-monthly`, { params: query })
            .then(handleData)
            .catch(handleError);
    }
    getServicePassengers(query: { serviceIds?: string; travelfileId?: string; serviceType?: string }) {
        return request
            .get(`${url}-service-passengers`, { params: query })
            .then(handleData)
            .catch(handleError);
    }
    getHotelDetails(query: { serviceIds: string }) {
        return request.get(`${url}-hotel-details`, { params: query }).then(handleData).catch(handleError);
    }
    updateTravelFileService(body: BuiltUpdateTravelFileService) {
        clearTravelFilesQueryCache();
        return request.put(`${url}-update-service`, body).then(handleData).catch(handleError);
    }
    amendTravelFileService(body: BodyAmendTravelFileService) {
        clearTravelFilesQueryCache();
        return request.put(`${url}-amend-service`, body).then(handleData).catch(handleError);
    }
    amendServiceDates(body: {
        travelFileServiceId: string | number;
        startDate: string;
        endDate: string;
        currUserDetails?: { id?: string; name?: string; email?: string };
    }) {
        clearTravelFilesQueryCache();
        return request.put(`${url}-amend-service-dates`, body).then(handleData).catch(handleError);
    }
    addServiceToTravelFile(body: BodyAddServiceToTravelFile) {
        clearTravelFilesQueryCache();
        return request.put(`${url}-add-service`, body).then(handleData).catch(handleError);
    }
    updateRoomsGroupTravelFile(body: BodyUpdateRoomsGroupTravelFile) {
        clearTravelFilesQueryCache();
        return request.put(`${url}-group-rooms-update`, body).then(handleData).catch(handleError);
    }
    checkRoomsGroupTravelFilePrice(body: BodyUpdateRoomsGroupTravelFile): Promise<{ mismatches: RoomPriceMismatch[] }> {
        return request.post(`${url}-group-rooms-price-check`, body).then(handleData).catch(handleError);
    }
    updateTravelFileRemarks(body: BodyUpdateTravelFileRemarks) {
        return request.put(`${url}-update-remarks`, body).then(handleData).catch(handleError);
    }
    registerPassengersUpdateTourAndTravelFile(body: any) {
        clearTravelFilesQueryCache();
        return request.put(`${url}-tour-registration`, body).then(handleData).catch(handleError);
    }

    fetchLatestTravelFiles() {
        return request.post(`${url}-latest`).then(handleData).catch(handleError);
    }
    getGroupTravelFilesByTourDetails(query: any) {
        return request
            .get(`${url}-search-groups-by-tour`, { params: query })
            .then((res) => handleData(res))
            .catch((error) => handleError(error));
    }

    getFullService(serviceId: string) {
        return request
            .get(`${url}-get-service`, { params: { serviceId } })
            .then(handleData)
            .catch(handleError);
    }

    addTravelFileLog(body: { travelFileId: string; type: string; body: string; user: string }) {
        return request.put(`${url}-add-log`, body).then(handleData).catch(handleError);
    }

    getTourPriceBreakdown(travelFileId: string | number): Promise<TourPriceBreakdown[]> {
        return request
            .get(`${url}-tour-price-breakdown`, { params: { travelFileId } })
            .then(handleData)
            .catch(handleError);
    }

    /** With includeClients on a group file, its client files' logs come too (rows carry travelFileRefNumber). */
    getTravelFileLogs(travelFileId: string, includeClients = false) {
        return request
            .get(`${url}-get-logs`, { params: includeClients ? { travelFileId, includeClients } : { travelFileId } })
            .then(handleData)
            .catch(handleError);
    }

    getReservationHistory(travelFileId: string, serviceId?: string) {
        const params: Record<string, string> = { travelFileId };
        if (serviceId) params.serviceId = serviceId;
        return request.get(`${url}-reservation-history`, { params }).then(handleData).catch(handleError);
    }

    closeTravelFile(body: any) {
        clearTravelFilesQueryCache();
        return request.post(`${url}-close`, body).then(handleData).catch(handleError);
    }

    reopenTravelFile(body: { travelFileId: string }) {
        clearTravelFilesQueryCache();
        return request.put(`${url}-reopen`, body).then(handleData).catch(handleError);
    }

    deleteService(body: { travelFileServiceId: string }) {
        clearTravelFilesQueryCache();
        return request.put(`${url}-delete-service`, body).then(handleData).catch(handleError);
    }
    restoreService(body: { travelFileServiceId: string }) {
        clearTravelFilesQueryCache();
        return request.put(`${url}-restore-service`, body).then(handleData).catch(handleError);
    }

    /**
     * Soft-delete a travel file. Only allowed for empty non-group TFs (no
     * active services, no active documents). Backend returns 409 with
     * { message, reasons[] } when blocked — let the caller surface the reasons.
     */
    deleteTravelFile(body: {
        travelFileId: string | number;
        currUserDetails?: { id?: string; name?: string; email?: string };
    }) {
        clearTravelFilesQueryCache();
        return request.put(`${url}-delete`, body).then(handleData).catch(handleError);
    }

    /**
     * Cancel a travel file (Open / In Process → Cancelled only). Native,
     * individual (client) travel files only: the backend returns 403 for an
     * imported file, 409 for a group file or one in any other status (with the
     * current `status`), 404 when it does not exist.
     */
    cancelTravelFile(body: { travelFileId: string | number }): Promise<{
        _id: string | number;
        status: string;
        updatedAt: string;
    }> {
        clearTravelFilesQueryCache();
        return request.put(`${url}-cancel`, body).then(handleData).catch(handleError);
    }

    /**
     * Undo a cancellation (Cancelled → Open when the customer has paid, else
     * In Process). Same scope and refusals as {@link cancelTravelFile}; 409 when
     * the file is not Cancelled. The response carries the restored `status`.
     */
    undoCancelTravelFile(body: { travelFileId: string | number }): Promise<{
        _id: string | number;
        status: string;
        updatedAt: string;
    }> {
        clearTravelFilesQueryCache();
        return request.put(`${url}-undo-cancel`, body).then(handleData).catch(handleError);
    }

    /**
     * Set, change or clear (travelAgencyAccountId: null) the travel agent of a
     * native client travel file. 403 imported; 409 group / Closed / Cancelled;
     * 409 { code: 'TRAVEL_FILE_HAS_DOCUMENTS', documentsCount } until the call is
     * repeated with confirmHasDocuments: true.
     */
    updateTravelAgent(body: {
        travelFileId: string | number;
        travelAgencyAccountId: string | number | null;
        confirmHasDocuments?: boolean;
    }): Promise<{
        _id: string | number;
        travelAgencyAccount: number | null;
        travelAgencyAccountName: string | null;
        unchanged?: boolean;
    }> {
        clearTravelFilesQueryCache();
        return request.put(`${url}-travel-agent`, body).then(handleData).catch(handleError);
    }

    async getClientTravelFilesByGroupId(groupId: string) {
        try {
            const res = await request.get(`${url}-clients-by-group/${groupId}`);
            return handleData(res);
        } catch (error) {
            return handleError(error);
        }
    }
}

const api = new ExtendedApi(url);

export default api;
